import React, { useState, useEffect } from "react";
import { message } from "antd";
import { useTheme } from "../../ThemeContext";
import { becomeNewCustomer } from "../../utils/apiClient";
import { LOGIN_TYPEWRITER_LINES } from "../../utils/constants/typewriterTexts";
import TermsAndConditionsPopup from "./TermsAndConditionsPopup";
import TypewriterText from "./TypewriterText";
import { 
  cleanPhoneNumber, 
  isValidUSPhoneNumber 
} from "../../utils/helpers/format-phone-number";
import "./auth-login.css";

const SignUp = () => {
  const { themeMode } = useTheme();

  // Sign-up specific state
  const [signUpFormData, setSignUpFormData] = useState({
    organizationName: "",
    organizationType: "",
    contactName: "",
    phoneNumber: "",
    email: "",
  });
  const [signUpErrors, setSignUpErrors] = useState({});
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Handle Enter key press
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "Enter" && !isLoading) {
        handleSignUpSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isLoading, signUpFormData]);

  // Input validation functions
  const validateEmail = (email) => {
    const emailRegex = /^.{1,64}@[^\s@]+(\.[^\s@]+){1,255}$/;
    if (!email.trim()) {
      return "Email is required";
    }
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return null;
  };

  // Sign-up validation functions
  const validateSignUpForm = () => {
    const newErrors = {};
    
    // Organization Name validation
    if (!signUpFormData.organizationName.trim()) {
      newErrors.organizationName = "Organization name is required";
    }
    
    // Organization Type validation
    if (!signUpFormData.organizationType.trim()) {
      newErrors.organizationType = "Organization type is required";
    }
    
    // Contact Name validation
    if (!signUpFormData.contactName.trim()) {
      newErrors.contactName = "Contact name is required";
    }
    
    // Phone Number validation
    if (!signUpFormData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!isValidUSPhoneNumber(signUpFormData.phoneNumber)) {
      newErrors.phoneNumber = "Enter a valid US phone number";
    }
    
    // Email validation
    const emailError = validateEmail(signUpFormData.email);
    if (emailError) newErrors.email = emailError;

    setSignUpErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle key events for phone number inputs
  const handlePhoneKeyDown = (event) => {
    const input = event.target;
    const cursorPosition = input.selectionStart || 0;
    
    // Prevent deletion or modification of +1 prefix
    if ((event.key === 'Backspace' || event.key === 'Delete') && cursorPosition <= 3) {
      event.preventDefault();
      return;
    }
    
    // Prevent moving cursor into the +1 prefix with arrow keys
    if (event.key === 'ArrowLeft' && cursorPosition <= 3) {
      event.preventDefault();
      return;
    }
    
    // Prevent Home key from going to beginning of +1
    if (event.key === 'Home') {
      event.preventDefault();
      setTimeout(() => {
        if (input) {
          input.setSelectionRange(3, 3);
        }
      }, 0);
      return;
    }
    
    // Only allow numbers and navigation keys
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Tab', 'Enter', 'Escape'
    ];
    
    // Allow Ctrl+A, Ctrl+C, Ctrl+V, etc.
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    
    if (!allowedKeys.includes(event.key) && !/\d/.test(event.key)) {
      event.preventDefault();
    }
  };

  // Handle click events for phone number inputs
  const handlePhoneClick = (event) => {
    const input = event.target;
    setTimeout(() => {
      const cursorPosition = input.selectionStart || 0;
      if (cursorPosition < 3) {
        input.setSelectionRange(3, 3);
      }
    }, 0);
  };

  // Handle focus events for phone number inputs
  const handlePhoneFocus = (event) => {
    const input = event.target;
    setTimeout(() => {
      // Set cursor to after +1 prefix if the field is empty or cursor is in prefix
      if (!signUpFormData.phoneNumber || input.selectionStart < 3) {
        input.setSelectionRange(3, 3);
      }
    }, 0);
  };

  // Sign-up form handlers
  const handleSignUpInputChange = (field, value) => {
    // Special handling for phone number field to provide formatting
    if (field === 'phoneNumber') {
      // Remove the +1 prefix if user tries to modify it
      const valueWithoutPrefix = value.replace(/^\+1\s*/, '');
      
      // Remove all non-digit characters for processing
      const cleaned = valueWithoutPrefix.replace(/\D/g, '');
      
      // Format as (XXX) XXX-XXXX if we have enough digits
      let formatted = '';
      if (cleaned.length === 0) {
        formatted = '';
      } else if (cleaned.length <= 3) {
        formatted = cleaned;
      } else if (cleaned.length <= 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
      
      setSignUpFormData(prev => ({ ...prev, [field]: formatted }));
    } else {
      setSignUpFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear error when user starts typing
    if (signUpErrors[field]) {
      setSignUpErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSignUpSubmit = async () => {
    if (!validateSignUpForm()) return;

    setIsLoading(true);
    
    try {
      message.loading("Submitting your details...", 0);
      
      // Prepare data with cleaned phone number
      const submitData = {
        ...signUpFormData,
        phoneNumber: cleanPhoneNumber(signUpFormData.phoneNumber)
      };
      
      // Submit customer sign-up data to API
      const response = await becomeNewCustomer(submitData);
      
      message.destroy();
      
      if (response.statusCode === "SUCCESS") {
        message.success("Thank you for submitting your details!");
        // Show success screen
        setSignUpSuccess(true);
      } else {
        message.error(response.message || "Something went wrong. Please try again later.");
      }
      
    } catch (error) {
      message.destroy();
      console.error("Error submitting customer details:", error);
      message.error(error.response?.data?.error || error.response?.data?.message || "Something went wrong. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className={`auth-login-container auth-login-container-responsive ${themeMode}`}
      style={{
        backgroundColor: themeMode === 'light' ? '#ffffff' : '#04002b'
      }}
    >
      {/* Left Column - Form */}
      <div className="auth-login-form-section auth-login-form-section-responsive">
        <div className="auth-login-form-content auth-login-form-content-responsive">
          {/* Conditional rendering based on sign-up success */}
          {signUpSuccess ? (
            // Success Screen
            <div className="auth-success-container auth-success-container-responsive">
              <div className="auth-success-content auth-success-content-responsive">
                <img
                  src={`${process.env.PUBLIC_URL}/login/voicecare-logo.svg`}
                  alt="VoiceCare AI"
                  className="auth-success-logo auth-success-logo-responsive"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div className="auth-success-icon auth-success-icon-responsive">
                  <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="30" cy="30" r="30" fill="#10B981"/>
                    <path d="M20 30L26 36L40 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h2 className="auth-success-title auth-success-title-responsive">Thank you for submitting your details!</h2>
                <p className="auth-success-subtitle auth-success-subtitle-responsive">
                  We've received your information.<br/>
                  Our team will reach out to you in 24hrs.
                </p>
                <button
                  className="auth-success-button auth-success-button-responsive"
                  onClick={() => window.location.href = 'https://voicecare.ai'}
                >
                  Go to Website
                </button>
              </div>
            </div>
          ) : (
            // Sign Up Form
            <>
              <h1 className="auth-login-title auth-login-title-responsive">Become a Customer</h1>
              {/* <p className="auth-login-subtitle auth-login-subtitle-responsive">Please fill in the following details</p> */}
              
              <div className="auth-login-form auth-login-form-responsive">
                {/* Organization Details Section */}
                <div className="auth-form-section auth-form-section-responsive">
                  <h3 className="auth-form-section-title auth-form-section-title-responsive">Organization Details</h3>
                  
                  <div className="auth-login-field auth-login-field-responsive">
                    <label className="auth-login-label auth-login-label-responsive">Name*</label>
                    <input
                      type="text"
                      placeholder="Name of your Organization"
                      value={signUpFormData.organizationName}
                      onChange={(e) => handleSignUpInputChange("organizationName", e.target.value)}
                      className={`auth-login-input auth-login-input-responsive ${signUpErrors.organizationName ? "error" : ""}`}
                      disabled={isLoading}
                    />
                    <div className="auth-login-error-container auth-login-error-container-responsive">
                      {signUpErrors.organizationName && (
                        <span className="auth-login-error auth-login-error-responsive">{signUpErrors.organizationName}</span>
                      )}
                    </div>
                  </div>

                  <div className="auth-login-field auth-login-field-responsive">
                    <label className="auth-login-label auth-login-label-responsive">Organization Type*</label>
                    <select
                      value={signUpFormData.organizationType}
                      onChange={(e) => handleSignUpInputChange("organizationType", e.target.value)}
                      className={`auth-login-input auth-login-select auth-login-input-responsive auth-login-select-responsive ${signUpErrors.organizationType ? "error" : ""}`}
                      disabled={isLoading}
                    >
                      <option value="">Organization Type</option>
                      <option value="type1">Individual Practice</option>
                      <option value="type2">SMBs</option>
                      <option value="type3">Enterprise</option>
                    </select>
                    <div className="auth-login-error-container auth-login-error-container-responsive">
                      {signUpErrors.organizationType && (
                        <span className="auth-login-error auth-login-error-responsive">{signUpErrors.organizationType}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Person Details Section */}
                <div className="auth-form-section auth-form-section-responsive">
                  <h3 className="auth-form-section-title auth-form-section-title-responsive">Contact Person Details</h3>
                  
                  <div className="auth-login-field auth-login-field-responsive">
                    <label className="auth-login-label auth-login-label-responsive">Name*</label>
                    <input
                      type="text"
                      placeholder="Contact person's first and last name"
                      value={signUpFormData.contactName}
                      onChange={(e) => handleSignUpInputChange("contactName", e.target.value)}
                      className={`auth-login-input auth-login-input-responsive ${signUpErrors.contactName ? "error" : ""}`}
                      disabled={isLoading}
                    />
                    <div className="auth-login-error-container auth-login-error-container-responsive">
                      {signUpErrors.contactName && (
                        <span className="auth-login-error auth-login-error-responsive">{signUpErrors.contactName}</span>
                      )}
                    </div>
                  </div>

                  <div className="auth-login-field auth-login-field-responsive">
                    <label className="auth-login-label auth-login-label-responsive">Phone Number*</label>
                    <input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={signUpFormData.phoneNumber ? `+1 ${signUpFormData.phoneNumber}` : "+1 "}
                      onChange={(e) => handleSignUpInputChange("phoneNumber", e.target.value)}
                      className={`auth-login-input auth-login-input-responsive ${signUpErrors.phoneNumber ? "error" : ""}`}
                      disabled={isLoading}
                      onKeyDown={(e) => handlePhoneKeyDown(e)}
                      onClick={(e) => handlePhoneClick(e)}
                      onFocus={(e) => handlePhoneFocus(e)}
                    />
                    <div className="auth-login-error-container auth-login-error-container-responsive">
                      {signUpErrors.phoneNumber && (
                        <span className="auth-login-error auth-login-error-responsive">{signUpErrors.phoneNumber}</span>
                      )}
                    </div>
                  </div>

                  <div className="auth-login-field auth-login-field-responsive">
                    <label className="auth-login-label auth-login-label-responsive">Email*</label>
                    <input
                      type="email"
                      placeholder="Contact person's email"
                      value={signUpFormData.email}
                      onChange={(e) => handleSignUpInputChange("email", e.target.value)}
                      className={`auth-login-input auth-login-input-responsive ${signUpErrors.email ? "error" : ""}`}
                      disabled={isLoading}
                    />
                    <div className="auth-login-error-container auth-login-error-container-responsive">
                      {signUpErrors.email && (
                        <span className="auth-login-error auth-login-error-responsive">{signUpErrors.email}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  className="auth-login-submit-button auth-login-submit-button-responsive"
                  onClick={handleSignUpSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Column - Hero */}
      <div className="auth-login-hero-section auth-login-hero-section-responsive">
        <div 
          className="auth-login-hero-background auth-login-hero-background-responsive"
          style={{
            position: 'absolute',
            top: '2rem',
            right: '2rem',
            bottom: '2rem',
            left: '2rem',
            backgroundImage: `url(${process.env.PUBLIC_URL}/login/login-background.png)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            borderRadius: '24px',
            zIndex: 1
          }}
        />
        {/* Logo positioned in top right corner */}
        <div className="auth-login-corner-logo auth-login-corner-logo-responsive">
          <img
            src={`${process.env.PUBLIC_URL}/login/login-logo.png`}
            alt="VoiceCare Logo"
            className="auth-login-corner-logo-img auth-login-corner-logo-img-responsive"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="auth-login-hero-content auth-login-hero-content-responsive">
          <img
            src={`${process.env.PUBLIC_URL}/login/voicecare-logo.svg`}
            alt="VoiceCare AI"
            className="auth-login-hero-logo auth-login-hero-logo-responsive"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <TypewriterText 
            lines={LOGIN_TYPEWRITER_LINES}
            speed={50}
            deleteSpeed={30}
            delay={1000}
            pauseBetweenCycles={2000}
            className="auth-login-hero-title auth-login-hero-title-responsive"
            showCursor={true}
            cycle={true}
          />
        </div>
      </div>

      <TermsAndConditionsPopup />
    </div>
  );
};

export default SignUp; 